import { Auth } from '@zocks-communications/client-sdk-js'
import { useAppSelector } from '../../store/hooks'
import { selectAuthenticatedUserToken, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetResolvedQuotasQuery } from '../../store/account/account'
import { CustomAuthAction, QuotaDescriptor, ScopeDescriptor } from './models'
import { useCallback } from 'react'
import { useGetBillingCustomerSessionAllocationAvailableQuery } from '../../store/plan/plan'

export const useZocksAuth = () => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const authenticatedUserToken = useAppSelector(selectAuthenticatedUserToken)
  if (!authenticatedUserToken) {
    throw Error('useZocksAuth needs to be authenticated')
  }
  const zocksAuth = new Auth({ accountId: authenticatedUser.accountId, apiToken: authenticatedUserToken.accessToken })
  const { hasScope } = zocksAuth
  return {
    hasScope,
  }
}

export const useZocksQuota = (options: { pollingInterval: number; refetchQuota: boolean | number } = { pollingInterval: 0, refetchQuota: 5 }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { pollingInterval, refetchQuota } = options
  const {
    data: quotas,
    isSuccess: quotaSuccess,
    isFetching,
  } = useGetResolvedQuotasQuery({ accountId: authenticatedUser.accountId }, { refetchOnMountOrArgChange: refetchQuota, pollingInterval })

  const getValue = (obj: any, path: string): any => {
    if (!path) {
      return obj
    }
    const properties = path.split('.')
    return getValue(obj[properties.shift()!], properties.join('.'))
  }

  const hasQuota = (quota: string): boolean => {
    if (quotas && quotas.data) {
      return getValue(quotas.data, quota)
    }
    return false
  }

  const getQuotaValue = (quota: string): any => {
    if (quotas && quotas.data) {
      return getValue(quotas.data, quota)
    }
    return null
  }

  const atQuotaLimit = (currentQuota: string, limitQuota: string) => {
    if (quotaSuccess) {
      const { value: currentValue } = getValue(quotas.data, currentQuota)
      const { value: limit } = getValue(quotas.data, limitQuota)
      return currentValue === limit
    }
    return false
  }

  const validateQuotaValue = (quotaPath: string, input: any): boolean => {
    if (!quotas?.data) {
      return false
    }
    const quota = getValue(quotas.data, quotaPath)
    if (!quota) {
      return false
    }
    const { value, validator } = quota
    if (!Array.isArray(value)) {
      return false
    }
    if (validator === 'listNotContains') {
      return !value.includes(input)
    }
    if (validator === 'listContains') {
      return value.includes(input)
    }
    return false
  }

  return {
    validateQuotaValue,
    atQuotaLimit,
    quotaSuccess,
    quotas,
    hasQuota,
    getQuotaValue,
    getValue,
    isFetching,
  }
}

export const useUserHasScopedBlockAccess = () => {
  const zocksAuth = useZocksAuth()
  const zocksQuota = useZocksQuota({ refetchQuota: false, pollingInterval: 0 })

  const userHasScopedBlockAccess = useCallback(
    (authActions: any[] = [], quota?: QuotaDescriptor, scope?: ScopeDescriptor) => {
      const [hasScope = zocksAuth.hasScope, hasQuota = zocksQuota.hasQuota] = authActions

      const accessChecks = []

      if (authActions.length === 1 && typeof authActions[0] === 'function') {
        accessChecks.push({
          type: 'custom',
          result: (authActions[0] as CustomAuthAction)(),
        })
      }

      if (quota) {
        accessChecks.push({
          type: 'quota',
          result: hasQuota(quota.quota),
        })
      }

      if (scope) {
        accessChecks.push({
          type: 'scope',
          result: hasScope(scope.resource || '**', scope.scope, scope.superScope),
        })
      }

      return [
        accessChecks.every((check) => check.result === true),
        {
          custom: accessChecks.find((ac) => ac.type === 'custom')?.result ?? false,
          quota: accessChecks.find((ac) => ac.type === 'quota')?.result ?? false,
          scope: accessChecks.find((ac) => ac.type === 'scope')?.result ?? false,
        },
      ]
    },
    [zocksAuth, zocksQuota],
  )

  return { userHasScopedBlockAccess }
}

export const useSessionAvailability = (options: { refetchAvailability: boolean | number } = { refetchAvailability: 5 }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { refetchAvailability } = options
  const {
    data: availability,
    isFetching,
    isSuccess,
  } = useGetBillingCustomerSessionAllocationAvailableQuery({ accountId: authenticatedUser.accountId }, { refetchOnMountOrArgChange: refetchAvailability })

  const available = availability?.data?.allowed ?? false
  const reason = availability?.data?.reason ?? ''
  const maxSessionsInPeriod = availability?.data?.maxSessionsInPeriod ?? null
  const usedSessionsInPeriod = availability?.data?.usedSessionsInPeriod ?? 0

  return { available, reason, maxSessionsInPeriod, usedSessionsInPeriod, isFetching, isSuccess }
}
